/**
 * The 24 staff-facing portal consoles, transcribed from `.railway/railway.ts`
 * (each gateway's own `PORTAL_BASE_URL`) — except Revenue (SRC) and
 * Registration & Land Division, which are SeyTax and onegov-registration,
 * standalone systems deployed outside this monorepo's Railway fleet (see
 * ~/Projects/seytax and ~/Projects/onegov-registration; M08 and M13/M18 were
 * deliberately skipped in the onegov M-series build since these systems
 * already cover them). Descriptions are one-line summaries of what each
 * portal actually proxies to, sourced from the proxied modules' own
 * `openapi.yaml` info blocks or `prisma/schema.prisma` header comments —
 * not invented.
 *
 * Deliberately excluded (out of scope for this directory):
 *  - the 5 citizen-facing self-service portals (citizen-civil-gateway etc.)
 *  - seyx-gateway, the stateless M2M data-plane proxy (not a staff console)
 *  - SeyTax's own taxpayer-facing portal (seytax-portal.vercel.app) and
 *    onegov-registration's own citizen portal (registry.onegovsc.com) —
 *    only their officer consoles belong in a staff directory
 */

export interface Portal {
  /** Short display name. */
  name: string;
  /** Real production URL from the gateway's PORTAL_BASE_URL. */
  url: string;
  /** One-line, source-grounded description of what the portal covers. */
  description: string;
}

export interface PortalGroup {
  title: string;
  blurb: string;
  portals: Portal[];
}

export const PORTAL_GROUPS: PortalGroup[] = [
  {
    title: 'Ministries',
    blurb: 'Line-ministry and ministry-equivalent staff consoles, one per portfolio.',
    portals: [
      {
        name: 'Home Affairs',
        url: 'https://homeaffairs.onegovsc.com',
        description:
          'Population register & civil status, residency/immigration, passports, police services, fire & rescue, and prisons & probation.',
      },
      {
        name: 'Justice',
        url: 'https://justice.onegovsc.com',
        description:
          'Judiciary e-filing & case management across all six court divisions, plus Attorney General legislation drafting and the Gazette.',
      },
      {
        name: 'Trade & Investment',
        url: 'https://trade.onegovsc.com',
        description:
          'Public e-procurement (e-GP), trade & investment services, and business/professional licensing (SLA).',
      },
      {
        name: 'Health',
        url: 'https://health.onegovsc.com',
        description:
          'Patient services & eHealth (records, appointments, e-prescriptions) plus Public Health Authority licensing and inspection.',
      },
      {
        name: 'Labour',
        url: 'https://labour.onegovsc.com',
        description:
          'Labour relations & compliance — worker grievances, workplace inspections, and OSH incident reporting under the Employment Act.',
      },
      {
        name: 'Education',
        url: 'https://education.onegovsc.com',
        description:
          'Admissions, student records, exams and scholarships, plus tertiary/TVET qualifications and professional registration (SQA).',
      },
      {
        name: 'Transport',
        url: 'https://transport.onegovsc.com',
        description:
          'Land transport & driver/vehicle licensing (LTA), maritime & ports (SMSA/SPA), and civil aviation (SCAA).',
      },
      {
        name: 'Local Government',
        url: 'https://localgov.onegovsc.com',
        description:
          'The 27 districts as service points, district office rosters, and participatory community budgeting.',
      },
      {
        name: 'Youth, Sports & Culture',
        url: 'https://youth.onegovsc.com',
        description:
          'Facility bookings, sport federation & athlete affiliation registers, the grant lifecycle, and the artist registry.',
      },
      {
        name: 'Blue Economy',
        url: 'https://blueeconomy.onegovsc.com',
        description:
          'Fisheries (SFA) vessel licensing & catch data, agriculture & bio-security, and blue-economy marine concessions/grants.',
      },
      {
        name: 'Foreign Affairs',
        url: 'https://foreignaffairs.onegovsc.com',
        description:
          'Consular services, diaspora engagement, overseas mission directory, and document legalisation.',
      },
      {
        name: 'Environment',
        url: 'https://environment.onegovsc.com',
        description:
          'Environment & climate (EIA, permits, protected areas), energy & utilities regulation, and disaster risk management (DRDM).',
      },
      {
        name: 'Governance',
        url: 'https://governance.onegovsc.com',
        description:
          'Elections administration (roll integrity, nominations, tallying) and the Ombudsman/Anti-Corruption Commission complaints office.',
      },
      {
        name: 'Land Use',
        url: 'https://landuse.onegovsc.com',
        description:
          'Planning authority development control, government housing/land-bank allocation, and infrastructure & public works permits.',
      },
      {
        name: 'Social Protection (ASP)',
        url: 'https://welfare.onegovsc.com',
        description:
          'Beneficiary registry, statutory social-security benefits, welfare assistance, homecare, medical board, and Family Affairs & Child Protection.',
      },
      {
        name: 'Pensions (SPF)',
        url: 'https://pension.onegovsc.com',
        description:
          'Member registry, employer contribution collection, benefits/pensions administration, voluntary savings, investments, and actuarial valuation.',
      },
      {
        name: 'Revenue (SRC)',
        url: 'https://seytax-console.vercel.app',
        description:
          'Taxpayer registration & TIN issuance, presumptive/business tax and VAT/PAYE filing & assessment, audits, objections & tribunal, debt & enforcement, and treasury reconciliation.',
      },
      {
        name: 'Registration & Land Division',
        url: 'https://onegov-registration-console.vercel.app',
        description:
          'Company, business, and association incorporation & compliance, intellectual property registration, and Land Division — parcels, charges, surveys, valuations, state land, and planning/housing applications.',
      },
    ],
  },
  {
    title: 'Platform & Cross-Government Services',
    blurb: 'Shared back-office and platform consoles used across every ministry.',
    portals: [
      {
        name: 'GovFin',
        url: 'https://finance.onegovsc.com',
        description:
          'IFMIS: chart of accounts, budget prep, warrants, commitments, expenditure, Treasury Single Account payments, revenue, virements, payroll, debt, fiscal reporting, procurement, and fixed assets.',
      },
      {
        name: 'StaffDesk',
        url: 'https://staffdesk.onegovsc.com',
        description:
          'The government back-office shell — staff identity, roles & delegations, and the unified officer work inbox.',
      },
      {
        name: 'Sey-X',
        url: 'https://seyx.onegovsc.com',
        description:
          'DICT operations centre for the national data-exchange layer — routing table and traffic reporting for the Sey-X gateway.',
      },
      {
        name: 'ServiceDesk',
        url: 'https://servicedesk.onegovsc.com',
        description:
          'Physical-channel management — appointment bookings, walk-in queues, and assisted counter transactions across government offices.',
      },
      {
        name: 'OpenGov',
        url: 'https://transparency.onegovsc.com',
        description:
          'Transparency & open data — open data catalogue, budget explorer, FOI portal, public notices, and the AuditChain anchor bulletin.',
      },
      {
        name: 'Analytics',
        url: 'https://analytics.onegovsc.com',
        description: 'National command centre — cross-government case, payment, and service-usage analytics.',
      },
      {
        name: 'Parking Enforcement',
        url: 'https://parking.onegovsc.com',
        description:
          'M46 Parking & Contravention pilot — SLTA and Police officer console: plate/status checks, contravention issuance and case management, zone & tariff administration, and notice-chain verification.',
      },
    ],
  },
];
